<template>
    <div class="object-space-container">
        <!-- Header Section -->
        <div class="header-section">
            <div class="header-content">
                <div class="title-section">
                    <div class="title-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 7l9-4 9 4M3 7v10l9 4 9-4V7M3 7l9 4 9-4"/>
                            <path d="M9 11l3 3 3-3"/>
                        </svg>
                    </div>
                    <div class="title-text">
                        <h1>Rooms</h1>
                        <p>Manage your property rooms efficiently add, update, and organize room details with ease.</p>
                    </div>
                </div>
                <div class="header-actions">
                    <button class="back-btn" @click="goBack" aria-label="Go back">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M19 12H5m0 0l7 7m-7-7l7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Back
                    </button>
                    <button class="btn-primary" @click="showAddForm = true" aria-label="Add new room">
                        <font-awesome-icon :icon="['fas','plus']" />
                        Add New Room
                    </button>
                </div>
            </div>
        </div>

        <!-- Stats Section -->
        <div v-if="!isLoading && objectSpaces.length > 0" class="stats-section">
            <div class="stat-card">
                <div class="stat-icon total">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M3 7l9-4 9 4M3 7v10l9 4 9-4V7M3 7l9 4 9-4"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ totalCount || objectSpaces.length }}</h3>
                    <p>Total Rooms</p>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon available">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ getTotalAvailable() }}</h3>
                    <p>Available</p>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon booked">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ getTotalBooked() }}</h3>
                    <p>Booked</p>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon onboarded">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M12.5 7a4 4 0 100-8 4 4 0 000 8z"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ getTotalOnboarded() }}</h3>
                    <p>Onboarded</p>
                </div>
            </div>
            <div v-if="totalRoomstats && totalRoomstats.totalBeds" class="stat-card">
                <div class="stat-icon beds">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ totalRoomstats.totalBeds }}</h3>
                    <p>Total Beds</p>
                </div>
            </div>
            <div v-if="totalRoomstats && totalRoomstats.HELD !== undefined" class="stat-card">
                <div class="stat-icon held">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ totalRoomstats.HELD || 0 }}</h3>
                    <p>Held</p>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <main class="content-area">
            <!-- Loading State -->
            <div v-if="isLoading" class="loading-state">
                <div class="loading-spinner">
                    <div class="spinner-ring"></div>
                    <span class="loading-text">Loading rooms...</span>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="objectSpaces.length === 0" class="empty-state">
                <div class="empty-state-content">
                    <div class="empty-state-icon">
                        <img src="@/assets/room.svg" alt="No rooms illustration" class="empty-illustration-img" />
                    </div>
                    <h2 class="empty-state-title">No rooms yet</h2>
                    <p class="empty-state-description">Get started by adding your first room to manage your property effectively.</p>
                    <button class="btn-primary" @click="showAddForm = true">
                        <font-awesome-icon :icon="['fas','plus']" />
                        Add Your First Room
                    </button>
                </div>
            </div>

            <!-- Table for HOSTEL -->
            <div v-else-if="adminProfileData.adminType == 'HOSTEL'" class="rooms-table-container">
                <div class="table-header">
                    <h3 class="table-title">Room Management</h3>
                    <div class="table-stats">
                        <span class="stat-item">
                            <span class="stat-number">{{ totalCount || objectSpaces.length }}</span>
                            <span class="stat-label">Total Rooms</span>
                        </span>
                    </div>
                </div>

                <!-- Space type filter (1 Sharing, 2 Sharing, etc.) -->
                <div v-if="availableRoomTypes.length > 0" class="space-type-filter">
                    <button
                        type="button"
                        class="space-type-btn"
                        :class="{ active: !selectedSpaceType }"
                        :disabled="isTableLoading"
                        @click="selectSpaceType(null)">
                        ALL
                    </button>
                    <button
                        v-for="roomType in availableRoomTypes"
                        :key="roomType"
                        type="button"
                        class="space-type-btn"
                        :class="{ active: selectedSpaceType === roomType }"
                        :disabled="isTableLoading"
                        @click="selectSpaceType(roomType)">
                        {{ roomType.replace('S', ' Sharing') }}
                    </button>
                </div>

                <div class="table-wrapper table-wrapper-hostel">
                    <div v-if="isTableLoading" class="table-loading-overlay">
                        <div class="table-loading-spinner">
                            <div class="spinner-ring"></div>
                            <span class="loading-text">Loading rooms...</span>
                        </div>
                    </div>
                    <table class="modern-table">
                        <thead>
                            <tr>
                                <th class="col-index">#</th>
                                <th class="col-id">Room ID</th>
                                <th class="col-room">Room No</th>
                                <th class="col-price">Monthly Fee</th>
                                <th class="col-created">Created</th>
                                <th class="col-updated">Updated</th>
                                <th class="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in objectSpaces" :key="item.spaceroomId || item.roomno" class="table-row">
                                <td class="col-index">{{ index + 1 }}</td>
                                <td class="col-id">
                                    <code class="room-id-code">{{ item.spaceroomId || '--' }}</code>
                                </td>
                                <td class="col-room">
                                    <span class="room-number-badge">{{ item.roomno || '--' }}</span>
                                </td>
                                <td class="col-price">
                                    <span class="price-amount" v-if="item.monthlyfee">₹{{ item.monthlyfee }}</span>
                                    <span v-else class="no-data">--</span>
                                </td>
                                <td class="col-created">
                                    <span class="date-text">{{ formatDate(item.createdat) }}</span>
                                </td>
                                <td class="col-updated">
                                    <span class="date-text">{{ formatDate(item.updatedon) }}</span>
                                </td>
                                <td class="col-actions">
                                    <div class="action-buttons">
                                        <button
                                            class="btn-action btn-view"
                                            @click="viewSpaceVideo(item)"
                                            :title="'View videos'"
                                            aria-label="View videos">
                                            <font-awesome-icon :icon="['fas','video']" />
                                        </button>
                                        <button
                                            class="btn-action btn-delete"
                                            @click="promptDelete(item)"
                                            :title="'Delete room'"
                                            aria-label="Delete room">
                                            <font-awesome-icon :icon="['fas','trash']" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="objectSpaces.length === 0 && !isLoading && !isTableLoading" class="empty-row">
                                <td colspan="7" class="empty-state">
                                    <div class="empty-content">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <path d="M3 7l9-4 9 4M3 7v10l9 4 9-4V7M3 7l9 4 9-4"/>
                                            <path d="M9 11l3 3 3-3"/>
                                        </svg>
                                        <h3>No rooms found</h3>
                                        <p>Start by adding your first room</p>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Tables for other admin types (HOTEL, HOUSE, PROPERTY) -->
            <div v-else class="rooms-table-container">
                <div class="table-header">
                    <h3 class="table-title">Room Management</h3>
                    <div class="table-stats">
                        <span class="stat-item">
                            <span class="stat-number">{{ objectSpaces.length }}</span>
                            <span class="stat-label">Total Rooms</span>
                        </span>
                    </div>
                </div>

                <!-- HOTEL TABLE -->
                <div v-if="adminProfileData.adminType == 'HOTEL'" class="table-wrapper">
                    <table class="modern-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Room Id</th>
                                <th>Image</th>
                                <th>Title</th>
                                <th>Room Type</th>
                                <th>Room Category</th>
                                <th>Total Rooms</th>
                                <th>Object Id</th>
                                <th>Availability Id</th>
                                <th>Object Type</th>
                                <th>Room Capacity</th>
                                <th>Total Adults</th>
                                <th>Total Kids</th>
                                <th>Bed Type</th>
                                <th>Room Utensils</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in objectSpaces" :key="item.objectSpaceId" class="table-row">
                                <td>{{ index + 1 }}</td>
                                <td>{{ item.objectSpaceId }}</td>
                                <td><img :src="item.featuredImage" alt="Room" class="room-image" /></td>
                                <td>{{ item.spaceTitle }}</td>
                                <td>{{ item.spaceType }}</td>
                                <td>{{ item.spaceCategory }}</td>
                                <td>{{ item.spaceTotalRoom }}</td>
                                <td>{{ item.objectId }}</td>
                                <td>{{ item.spaceAvailabilityId }}</td>
                                <td>{{ item.objectType }}</td>
                                <td>{{ item.spaceCapacity }}</td>
                                <td>{{ item.totalNumberOfAdults }}</td>
                                <td>{{ item.totalNumberOfKids }}</td>
                                <td>{{ item.bedType }}</td>
                                <td>{{ item.roomUtensils }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- HOUSE -->
                <div v-if="adminProfileData.adminType == 'HOUSE'" class="table-wrapper">
                    <table class="modern-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Room Id</th>
                                <th>Image</th>
                                <th>Room Type</th>
                                <th>Room Category</th>
                                <th>Total Rooms</th>
                                <th>Monthly Fee</th>
                                <th>Object Id</th>
                                <th>Availability Id</th>
                                <th>Object Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in objectSpaces" :key="item.objectSpaceId" class="table-row">
                                <td>{{ index + 1 }}</td>
                                <td>{{ item.objectSpaceId }}</td>
                                <td><img :src="item.featuredImage" alt="Room" class="room-image" /></td>
                                <td>{{ item.spaceType }}</td>
                                <td>{{ item.spaceCategory }}</td>
                                <td>{{ item.spaceTotalRoom }}</td>
                                <td>₹{{ item.objectMonthlyFee }}</td>
                                <td>{{ item.objectId }}</td>
                                <td>{{ item.spaceAvailabilityId }}</td>
                                <td>{{ item.objectType }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- PROPERTY -->
                <div v-if="adminProfileData.adminType == 'PROPERTY'" class="table-wrapper">
                    <table class="modern-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Room Id</th>
                                <th v-if="adminProfileData.adminType != 'HOSTEL'">Image</th>
                                <th v-if="adminProfileData.adminType == 'HOTEL'">Title</th>
                                <th>Room Type</th>
                                <th>Room Category</th>
                                <th>Total Rooms</th>
                                <th v-if="adminProfileData.adminType == 'HOSTEL' || adminProfileData.adminType == 'HOUSE'">Monthly Fee</th>
                                <th>Object Id</th>
                                <th>Availability Id</th>
                                <th>Object Type</th>
                                <th v-if="adminProfileData.adminType == 'HOTEL'">Room Capacity</th>
                                <th v-if="adminProfileData.adminType == 'HOTEL'">Total Adults</th>
                                <th v-if="adminProfileData.adminType == 'HOTEL'">Total Kids</th>
                                <th v-if="adminProfileData.adminType == 'HOTEL' || adminProfileData.adminType == 'HOSTEL'">Bed Type</th>
                                <th v-if="adminProfileData.adminType == 'HOTEL' || adminProfileData.adminType == 'HOSTEL'">Room Utensils</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in objectSpaces" :key="item.objectSpaceId" class="table-row">
                                <td>{{ index + 1 }}</td>
                                <td>{{ item.objectSpaceId }}</td>
                                <td v-if="adminProfileData.adminType != 'HOSTEL'"><img :src="item.featuredImage" alt="Room" class="room-image" /></td>
                                <td v-if="adminProfileData.adminType == 'HOTEL'">{{ item.spaceTitle }}</td>
                                <td>{{ item.spaceType }}</td>
                                <td>{{ item.spaceCategory }}</td>
                                <td>{{ item.spaceTotalRoom }}</td>
                                <td v-if="adminProfileData.adminType == 'HOSTEL'">₹{{ objectData.roomtypeprice && objectData.roomtypeprice[item.spaceType] ? objectData.roomtypeprice[item.spaceType] : '-' }}</td>
                                <td v-if="adminProfileData.adminType == 'HOUSE'">₹{{ item.objectMonthlyFee }}</td>
                                <td>{{ item.objectId }}</td>
                                <td>{{ item.spaceAvailabilityId }}</td>
                                <td>{{ item.objectType }}</td>
                                <td v-if="adminProfileData.adminType == 'HOTEL'">{{ item.spaceCapacity }}</td>
                                <td v-if="adminProfileData.adminType == 'HOTEL'">{{ item.totalNumberOfAdults }}</td>
                                <td v-if="adminProfileData.adminType == 'HOTEL'">{{ item.totalNumberOfKids }}</td>
                                <td v-if="adminProfileData.adminType == 'HOTEL' || adminProfileData.adminType == 'HOSTEL'">{{ item.bedType }}</td>
                                <td v-if="adminProfileData.adminType == 'HOTEL' || adminProfileData.adminType == 'HOSTEL'">{{ item.roomUtensils }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </main>

        <!-- Add Room Form -->
        <AddNewSpace v-if="showAddForm" class="addnewspace" :objectSpaces="objectSpaces" :objectData="objectData"
            @close="showAddForm = false" @submit="handleAddRoom" />

        <!-- Confirm Delete Modal -->
        <div v-if="showDeleteDialog" class="modal-overlay" @click="cancelDelete">
            <div class="modal-content delete-modal" @click.stop>
                <div class="modal-header">
                    <div class="modal-icon delete-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                    </div>
                    <h3 class="modal-title">Delete Room</h3>
                    <p class="modal-description">This action cannot be undone. The room will be permanently removed from your property.</p>
                </div>
                <div class="modal-actions">
                    <button class="btn-secondary" @click="cancelDelete">Cancel</button>
                    <button class="btn-danger" @click="confirmDelete">
                        <font-awesome-icon :icon="['fas','trash']" />
                        Delete Room
                    </button>
                </div>
            </div>
        </div>

        <!-- Video Gallery Modal -->
        <div v-if="showVideoModal" class="modal-overlay" @click="closeVideoModal">
            <div class="modal-content video-modal" @click.stop>
                <div class="modal-header">
                    <h3 class="modal-title">Room Videos</h3>
                    <button class="modal-close" @click="closeVideoModal" aria-label="Close modal">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <div class="modal-body">
                    <div v-if="isVideoLoading" class="loading-state">
                        <div class="loading-spinner">
                            <div class="spinner-ring"></div>
                        </div>
                        <span>Loading videos...</span>
                    </div>

                    <div v-else-if="videoList.length === 0" class="empty-videos">
                        <div class="empty-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                <path d="M23 7a2 2 0 0 0-1-1.73l-7.88-4.35a2 2 0 0 0-2 0L4.24 5.27A2 2 0 0 0 3 7v10a2 2 0 0 0 1.24 1.73l7.88 4.35a2 2 0 0 0 2 0L23 18.73A2 2 0 0 0 24 17V7a2 2 0 0 0-1-1.73z"/>
                                <polygon points="3.27 6.96 12 12.01 20.73 6.96"/>
                                <line x1="12" y1="22.08" x2="12" y2="12"/>
                            </svg>
                        </div>
                        <h4>No videos available</h4>
                        <p>This room doesn't have any videos uploaded yet.</p>
                    </div>

                    <div v-else class="video-gallery">
                        <div v-for="(video, index) in videoList" :key="index" class="video-item">
                            <div class="video-player">
                                <iframe
                                    :src="video"
                                    frameborder="0"
                                    allowfullscreen
                                    title="Room video"
                                ></iframe>
                            </div>
                            <div class="video-controls">
                                <button class="btn-danger btn-sm" @click="deleteVideo(video)">
                                    <font-awesome-icon :icon="['fas','trash']" />
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="toast-container">
            <transition-group name="toast" tag="div">
                <div
                    v-for="toast in toasts"
                    :key="toast.id"
                    class="toast"
                    :class="[`toast-${toast.type}`]">
                    <p class="toast-message">{{ toast.message }}</p>
                    <button class="toast-close" @click="removeToast(toast.id)">x</button>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faTrash, faVideo } from '@fortawesome/free-solid-svg-icons';
import AddNewSpace from './AddNewSpace.vue';

library.add(faPlus, faTrash, faVideo);

export default {
    name: 'ObjectSpace',
    components: {
        FontAwesomeIcon,
        AddNewSpace,
    },
    props: {
        objectData: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            objectName: '',
            adminProfileData: {},
            showAddForm: false,
            objectSpaces: [],
            isLoading: false,
            isTableLoading: false,
            objectId: '',
            cacheDuration: 600000, // 10 minutes
            showDeleteDialog: false,
            roomToDelete: null,
            showVideoModal: false,
            videoList: [],
            currentSpaceId: null,
            currentSpaceObj: null,
            isVideoLoading: false,
            totalRoomstats: null,
            totalCount: 0,
            availableRoomTypes: [],
            selectedSpaceType: null,
            toasts: [],
        };
    },
    watch: {
        'objectData.availableRoomType': {
            immediate: true,
            handler(newTypes) {
                this.initializeAvailableRoomTypes(newTypes);
            }
        }
    },
    async mounted() {
        this.objectName = this.$route.params.objectName;
        this.objectId = this.$route.params.id;
        const adminProfileData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = adminProfileData ? JSON.parse(adminProfileData) : {};
        this.initializeAvailableRoomTypes(this.objectData?.availableRoomType);
        window.addEventListener('beforeunload', this.clearCache);
        await this.fetchObjectSpaces();
    },
    beforeUnmount() {
        window.removeEventListener('beforeunload', this.clearCache);
    },
    methods: {
        initializeAvailableRoomTypes(types) {
            const normalizedTypes = Array.isArray(types) ? types.filter(type => typeof type === 'string' && type.trim()) : [];
            this.availableRoomTypes = normalizedTypes;

            if (normalizedTypes.length === 0) {
                this.selectedSpaceType = null;
                return;
            }

            // Default to ALL (no spaceType filter).
            if (this.selectedSpaceType && !normalizedTypes.includes(this.selectedSpaceType)) {
                this.selectedSpaceType = null;
            }
        },
        goBack() {
            this.$router.go(-1);
        },
        handleAddRoom(formData) {
            this.$emit('addRoom', formData);
        },
        clearCache() {
            const prefix = `ROOM-${this.objectId}`;
            Object.keys(localStorage).filter(k => k.startsWith(prefix)).forEach(k => localStorage.removeItem(k));
        },
        selectSpaceType(roomType) {
            if (this.selectedSpaceType === roomType) return;
            this.selectedSpaceType = roomType;
            this.fetchObjectSpaces(true);
        },
        promptDelete(item) {
            this.roomToDelete = item;
            this.showDeleteDialog = true;
        },
        async confirmDelete() {
            this.isLoading = true;
            this.showDeleteDialog = false;
            try {
                await apiService.deleteSpaceRoom({
                    objectspaceId: this.roomToDelete.objectspaceId || this.roomToDelete.objectSpaceId,
                    roomno: this.roomToDelete.roomno,
                    objectType: this.roomToDelete.objectType
                });
                // Filter by spaceroomId for individual rooms, or objectspaceId for space-level items
                this.objectSpaces = this.objectSpaces.filter(space => {
                    if (this.roomToDelete.spaceroomId && space.spaceroomId) {
                        return space.spaceroomId !== this.roomToDelete.spaceroomId;
                    }
                    return space.objectspaceId !== this.roomToDelete.objectspaceId;
                });
                this.clearCache();
            } catch (error) {
                console.error(error);
            } finally {
                this.isLoading = false;
                this.showDeleteDialog = false;
                this.roomToDelete = null;
            }
        },
        cancelDelete() {
            this.showDeleteDialog = false;
            this.roomToDelete = null;
        },
        async fetchObjectSpaces(tableOnly = false) {
            if (tableOnly) {
                this.isTableLoading = true;
            } else {
                this.isLoading = true;
            }
            const isHostel = this.adminProfileData.adminType === 'HOSTEL';
            const hasSpaceTypeFilter = isHostel && !!this.selectedSpaceType;
            const spaceTypeSuffix = hasSpaceTypeFilter ? `-${this.selectedSpaceType}` : '';
            const cacheKey = `ROOM-${this.objectId}${spaceTypeSuffix}`;
            const cachedData = localStorage.getItem(cacheKey);
            if (cachedData) {
                const { data, totalRoomstats, totalCount, availableRoomTypes: cachedTypes, timestamp } = JSON.parse(cachedData);
                const now = new Date().getTime();
                if (now - timestamp < this.cacheDuration) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                    this.objectSpaces = data;
                    this.totalRoomstats = totalRoomstats || null;
                    this.totalCount = totalCount || 0;
                    if (cachedTypes && cachedTypes.length > 0 && this.availableRoomTypes.length === 0) {
                        this.availableRoomTypes = cachedTypes;
                    }
                    if (tableOnly) this.isTableLoading = false;
                    else this.isLoading = false;
                    return;
                }
            }
            try {
                let objectId = this.$route.params.id;
                const params = {
                    page: 1,
                    pagesize: 10,
                    objectType: this.adminProfileData.adminType,
                    objectId: objectId
                };
                if (hasSpaceTypeFilter) {
                    params.spaceType = this.selectedSpaceType;
                }
                let response = await apiService.getSpaceRooms(params);
                const data = response.data?.data || [];
                this.objectSpaces = data;
                this.totalRoomstats = response.data?.totalRoomstats || null;
                this.totalCount = response.data?.totalCount || 0;
                const cacheData = {
                    data: this.objectSpaces,
                    totalRoomstats: this.totalRoomstats,
                    totalCount: this.totalCount,
                    availableRoomTypes: isHostel ? this.availableRoomTypes : undefined,
                    timestamp: new Date().getTime()
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (error) {
                if (isNoDataError(error)) {
                    this.objectSpaces = [];
                    this.totalRoomstats = null;
                    this.totalCount = 0;
                } else if (isHmsSubscriptionInactiveError(error)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    console.error('Failed to fetch object spaces:', error.response?.data || error);
                }
            } finally {
                if (tableOnly) this.isTableLoading = false;
                else this.isLoading = false;
            }
        },
        async viewSpaceVideo(item) {
            this.isVideoLoading = true;
            this.showVideoModal = true;
            this.currentSpaceId = item.objectspaceId || item.objectSpaceId;
            this.currentSpaceObj = item;
            try {
                // First check if spaceVideo is already in the item
                if (item.spaceVideo) {
                    try {
                        // Parse if it's a JSON string
                        this.videoList = typeof item.spaceVideo === 'string' 
                            ? JSON.parse(item.spaceVideo) 
                            : item.spaceVideo;
                    } catch (parseError) {
                        // If parsing fails, try fetching from API
                let res = await apiService.getObjectSpaceById(this.currentSpaceId);
                        this.videoList = res.data?.spaceVideo || [];
                    }
                } else {
                    // Fetch from API if not in item
                    let res = await apiService.getObjectSpaceById(this.currentSpaceId);
                    this.videoList = res.data?.spaceVideo || [];
                }
            } catch (e) {
                // Handle "no data" case - set empty array without showing error
                if (isNoDataError(e)) {
                    this.videoList = [];
                } else if (isHmsSubscriptionInactiveError(e)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    this.videoList = [];
                    console.error('Error fetching space video:', e);
                }
            } finally {
                this.isVideoLoading = false;
            }
        },
        async deleteVideo(url) {
            if (!confirm('Delete this video?')) return;
            const updatedList = this.videoList.filter(v => v !== url);
            try {
                await apiService.updateObjectSpace(this.currentSpaceId, { spaceVideo: updatedList });
                this.videoList = updatedList;
            } catch (e) {
                // Skip error handling for "no data" cases (status 400 with errorcode 2003)
                if (isNoDataError(e) || isHmsSubscriptionInactiveError(e)) {
                    return;
                }
                
                let message = null;
                if (e?.response?.data) {
                    const errorData = e.response.data;
                    const status = e.response.status;
                    
                    // For HTTP 400 errors, always use the reason field from API response
                    if (status === 400) {
                        if (errorData.reason) {
                            message = errorData.reason;
                        }
                        // If no reason provided for 400 error, don't show toast (no generic messages)
                    } else {
                        // For other status codes, prioritize reason field
                        if (errorData.reason) {
                            message = errorData.reason;
                        } else if (errorData.message) {
                            message = errorData.message;
                        } else {
                            message = 'Failed to delete video';
                        }
                    }
                } else {
                    message = 'Failed to delete video';
                }
                
                // Only show toast if we have a message (genuine errors only)
                if (message) {
                    this.showToast(message, 'error');
                }
            }
        },
        showToast(message, type = 'error', duration = 5000) {
            const id = Date.now() + Math.random();
            this.toasts.push({ id, message, type });
            if (duration > 0) {
                setTimeout(() => this.removeToast(id), duration);
            }
        },
        removeToast(id) {
            this.toasts = this.toasts.filter(toast => toast.id !== id);
        },
        closeVideoModal() {
            this.showVideoModal = false;
            this.videoList = [];
            this.currentSpaceId = null;
        },
        getTotalAvailable() {
            if (this.totalRoomstats && this.totalRoomstats.AVAILABLE !== undefined) {
                return this.totalRoomstats.AVAILABLE;
            }
            // Fallback to calculating from individual items if API stats not available
            return this.objectSpaces.reduce((total, space) => {
                return total + (space.statusCounts?.AVAILABLE || 0);
            }, 0);
        },
        getTotalBooked() {
            if (this.totalRoomstats && this.totalRoomstats.BOOKED !== undefined) {
                return this.totalRoomstats.BOOKED;
            }
            // Fallback to calculating from individual items if API stats not available
            return this.objectSpaces.reduce((total, space) => {
                return total + (space.statusCounts?.BOOKED || 0);
            }, 0);
        },
        getTotalOnboarded() {
            if (this.totalRoomstats && this.totalRoomstats.ONBORDED !== undefined) {
                return this.totalRoomstats.ONBORDED;
            }
            // Fallback to calculating from individual items if API stats not available
            return this.objectSpaces.reduce((total, space) => {
                return total + (space.statusCounts?.ONBORDED || 0);
            }, 0);
        },
        formatDate(dateString) {
            if (!dateString) return '--';
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
    },
};
</script>

<style scoped>
/* Main Container */
.object-space-container {
    padding: 0;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    min-height: 100vh;
    box-sizing: border-box;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    display: flex;
    flex-direction: column;
}

/* Loading Overlay */
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
}

.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.spinner-ring {
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top: 4px solid #5b2e8e;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text {
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 500;
    margin-top: 1rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Header Section */
.header-section {
    background: white;
    border-radius: 0;
    padding: 16px 24px;
    margin-bottom: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: none;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 100;
    flex-shrink: 0;
    margin-top: 20px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    max-width: 1400px;
    margin: 0 auto;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.title-icon {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
    color: #ffffff;
    flex-shrink: 0;
}

.title-icon svg {
    width: 20px;
    height: 20px;
}

.title-text {
    text-align: left;
}

.title-text h1 {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    letter-spacing: -0.025em;
}

.title-text p {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    font-weight: 400;
}

.header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-shrink: 0;
}

.back-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #64748b;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.back-btn:hover {
    background: #f8fafc;
    border-color: #5b2e8e;
    color: #5b2e8e;
}

.back-btn svg {
    width: 20px;
    height: 20px;
}

/* Stats Section */
.stats-section {
    background: white;
    padding: 24px;
    margin-bottom: 24px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border-bottom: 1px solid #e2e8f0;
    width: 100%;
    box-sizing: border-box;
}

.stat-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.stat-icon.total {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: #ffffff;
}

.stat-icon.available {
    background: #ecfdf5;
    color: #065f46;
}

.stat-icon.booked {
    background: #fffbeb;
    color: #92400e;
}

.stat-icon.onboarded {
    background: #eff6ff;
    color: #1e40af;
}

.stat-icon.beds {
    background: #f3e8ff;
    color: #7c3aed;
}

.stat-icon.held {
    background: #fef2f2;
    color: #dc2626;
}

.stat-icon svg {
    width: 24px;
    height: 24px;
}

.stat-content {
    flex: 1;
    text-align: left;
}

.stat-content h3 {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px 0;
    line-height: 1;
}

.stat-content p {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    font-weight: 500;
}

/* Button Styles */
.btn-primary, .btn-secondary, .btn-danger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.btn-primary {
    background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
    color: #ffffff;
    box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.btn-secondary {
    background: #ffffff;
    color: #64748b;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-secondary:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
}

.btn-danger {
    background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-danger:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(220, 38, 38, 0.4);
}

.btn-sm {
    padding: 8px 16px;
    font-size: 14px;
}

.icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
}

/* Content Area */
.content-area {
    flex: 1;
    padding: 0 0 2rem;
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    max-width: 100%;
    margin: 0;
    width: 100%;
    box-sizing: border-box;
}

/* Loading State */
.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    margin-top: 2rem;
}

/* Empty State */
.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    margin-top: 2rem;
}

.empty-state-content {
    text-align: center;
    max-width: 400px;
    padding: 3rem 2rem;
}

.empty-state-icon {
    width: auto;
    height: auto;
    margin: 0 auto 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.empty-illustration-img {
    width: 300px;
    height: 300px;
    max-width: 100%;
    object-fit: contain;
}

.empty-state-title {
    margin: 0 0 0.75rem 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
}

.empty-state-description {
    margin: 0 0 2rem 0;
    font-size: 1rem;
    color: #64748b;
    line-height: 1.6;
}

/* Rooms Grid Container */
.rooms-grid-container {
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
}

.rooms-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
    width: 100%;
    box-sizing: border-box;
}

/* Room Card */
.room-card {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: default;
    position: relative;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
}

.room-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
    transform: scaleX(0);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.room-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.room-card:hover::before {
    transform: scaleX(1);
}

.card-header {
    padding: 1.5rem 1.5rem 1rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
}

.room-type-badge {
    background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
    color: #ffffff;
    padding: 0.5rem 1rem;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(91, 46, 142, 0.3);
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.card-menu {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
    align-items: center;
}

.menu-btn {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 0.5rem;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: #64748b;
}

.menu-btn.btn-view:hover {
    background: #e0f2fe;
    border-color: #bae6fd;
    color: #0369a1;
    transform: translateY(-1px);
}

.menu-btn.btn-delete:hover {
    background: #fee2e2;
    border-color: #fecaca;
    color: #dc2626;
    transform: translateY(-1px);
}

.card-body {
    padding: 0 1.5rem 1.5rem;
    width: 100%;
    box-sizing: border-box;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.room-info-section {
    margin-bottom: 1.5rem;
    width: 100%;
    box-sizing: border-box;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f1f5f9;
    width: 100%;
    box-sizing: border-box;
    gap: 0.5rem;
}

.info-row:last-child {
    border-bottom: none;
}

.info-label {
    font-size: 0.875rem;
    color: #64748b;
    font-weight: 500;
    flex-shrink: 0;
    min-width: 0;
}

.info-value {
    font-size: 0.875rem;
    color: #1e293b;
    font-weight: 600;
    text-align: right;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.id-value {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.8125rem;
    color: #64748b;
}

.price-value {
    color: #059669;
    font-size: 1rem;
}

/* Status Section */
.status-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 2px solid #f1f5f9;
    width: 100%;
    box-sizing: border-box;
}

.status-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #475569;
    margin: 0 0 1rem 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.status-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    width: 100%;
    box-sizing: border-box;
}

.status-item {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1rem;
    text-align: center;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
}

.status-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.status-item.available {
    border-color: #d1fae5;
    background: #ecfdf5;
}

.status-item.booked {
    border-color: #fde68a;
    background: #fffbeb;
}

.status-item.onboarded {
    border-color: #dbeafe;
    background: #eff6ff;
}

.status-item.held {
    border-color: #fecaca;
    background: #fef2f2;
}

.status-count {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.25rem;
    line-height: 1;
}

.status-item.available .status-count {
    color: #065f46;
}

.status-item.booked .status-count {
    color: #92400e;
}

.status-item.onboarded .status-count {
    color: #1e40af;
}

.status-item.held .status-count {
    color: #991b1b;
}

.status-label {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

/* Utensils Section */
.utensils-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 2px solid #f1f5f9;
    width: 100%;
    box-sizing: border-box;
}

.utensils-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #475569;
    margin: 0 0 0.75rem 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.utensils-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
    box-sizing: border-box;
}

.utensil-tag {
    background: #f1f5f9;
    color: #475569;
    padding: 0.375rem 0.75rem;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 500;
    border: 1px solid #e2e8f0;
}

/* Table Container */
.rooms-table-container {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
    margin: 0;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    flex-shrink: 0;
}

.table-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
}

.table-stats {
    display: flex;
    gap: 1.5rem;
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
}

.stat-number {
    font-size: 1.5rem;
    font-weight: 700;
    color: #5b2e8e;
}

.stat-label {
    font-size: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Space type filter (1 Sharing, 2 Sharing, etc.) */
.space-type-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    background: #fff;
}

.space-type-btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #64748b;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    transition: color 0.2s, background 0.2s, border-color 0.2s;
}

.space-type-btn:hover {
    color: #5b2e8e;
    background: #ede9fe;
    border-color: #c4b5fd;
}

.space-type-btn.active {
    color: #fff;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    border-color: #5b2e8e;
}

.table-wrapper-hostel {
    position: relative;
}

.table-loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: 0 0 8px 8px;
}

.table-loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
}

.table-loading-spinner .spinner-ring {
    width: 36px;
    height: 36px;
    border-width: 3px;
}

.table-loading-spinner .loading-text {
    margin-top: 0;
    font-size: 0.8125rem;
}

/* Table Wrapper */
.table-wrapper {
    overflow-x: auto;
    overflow-y: auto;
    min-height: 400px;
    flex: 1;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
    width: 100%;
    margin: 0;
    padding: 0;
}

.table-wrapper::-webkit-scrollbar {
    height: 8px;
    width: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.modern-table {
    width: 100%;
    min-width: 1400px;
    border-collapse: collapse;
    font-size: 14px;
    table-layout: fixed;
}

.modern-table thead {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.modern-table th {
    background: #f8fafc;
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: #475569;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 10;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
}

.modern-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.2s ease;
}

.modern-table tbody tr:hover {
    background: linear-gradient(135deg, #faf7ff, #f3f0ff);
}

.modern-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: middle;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    text-align: left;
    box-sizing: border-box;
}

/* Table Column Styles */
.modern-table th.col-index,
.modern-table td.col-index {
    width: 60px;
    min-width: 60px;
    max-width: 60px;
    text-align: center;
}

.modern-table td.col-index {
    font-weight: 600;
    color: #64748b;
}

.modern-table th.col-id,
.modern-table td.col-id {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
    text-align: center;
}

.modern-table th.col-room,
.modern-table td.col-room {
    width: 100px;
    min-width: 100px;
    max-width: 100px;
    text-align: center;
}

.modern-table th.col-price,
.modern-table td.col-price {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
    text-align: center;
}

.modern-table th.col-created,
.modern-table td.col-created,
.modern-table th.col-updated,
.modern-table td.col-updated {
    width: 160px;
    min-width: 160px;
    max-width: 160px;
    text-align: center;
}

.modern-table th.col-actions,
.modern-table td.col-actions {
    width: 140px;
    min-width: 140px;
    max-width: 140px;
    text-align: center;
}

/* Row Content Styles */
.row-number {
    font-weight: 600;
    color: #64748b;
}

.room-badge {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
    color: #ffffff;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.room-number-badge {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
    color: #ffffff;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    white-space: nowrap;
}

.type-badge {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    background: #f1f5f9;
    color: #475569;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    border: 1px solid #e2e8f0;
    white-space: nowrap;
}

.price-amount {
    font-weight: 600;
    color: #059669;
}

.space-id {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.8125rem;
    color: #64748b;
}

.room-id-code,
.space-id-code,
.object-id-code {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.8125rem;
    color: #64748b;
    background: #f8fafc;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

/* Code elements in cells */
.modern-table td.col-id code {
    display: inline-block;
}

.date-text {
    color: #64748b;
    font-size: 0.8125rem;
}

.no-data {
    color: #94a3b8;
    font-style: italic;
}

.empty-row {
    background: #ffffff;
}

.empty-state {
    padding: 3rem 2rem;
    text-align: center;
}

.empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.empty-content svg {
    width: 64px;
    height: 64px;
    color: #cbd5e1;
    margin-bottom: 0.5rem;
}

.empty-content h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #475569;
}

.empty-content p {
    margin: 0;
    font-size: 0.875rem;
    color: #64748b;
}

.room-image {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

/* Action Buttons */
.action-buttons {
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
}

.btn-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    background: transparent;
}

.btn-view {
    background: #f0f9ff;
    color: #0369a1;
    border: 1px solid #bae6fd;
}

.btn-view:hover {
    background: #e0f2fe;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(3, 105, 161, 0.2);
}

.btn-delete {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
}

.btn-delete:hover {
    background: #fee2e2;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

.action-icon {
    width: 18px;
    height: 18px;
}

/* FontAwesome icon styling */
.btn-action font-awesome-icon {
    font-size: 16px;
}

.btn-primary font-awesome-icon,
.btn-secondary font-awesome-icon,
.btn-danger font-awesome-icon {
    font-size: 16px;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 20px;
    backdrop-filter: blur(4px);
}

.modal-content {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    max-width: 90vw;
    max-height: 90vh;
    overflow: hidden;
    animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.delete-modal {
    max-width: 480px;
    width: 100%;
}

.video-modal {
    max-width: 1200px;
    width: 100%;
}

.modal-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
}

.modal-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.delete-icon {
    background: #fef2f2;
    color: #dc2626;
}

.modal-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    flex: 1;
}

.modal-description {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    line-height: 1.6;
}

.modal-close {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    border: none;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.modal-close:hover {
    background: #e2e8f0;
    color: #475569;
}

.modal-close svg {
    width: 20px;
    height: 20px;
}

.modal-body {
    padding: 24px;
    max-height: 60vh;
    overflow-y: auto;
}

.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding: 24px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
}

/* Video Gallery */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 48px 24px;
    color: #64748b;
}

.empty-videos {
    text-align: center;
    padding: 48px 24px;
}

.empty-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 16px;
    background: #f1f5f9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
}

.empty-icon svg {
    width: 32px;
    height: 32px;
}

.empty-videos h4 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
}

.empty-videos p {
    margin: 0;
    font-size: 14px;
    color: #64748b;
}

.video-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1.5rem;
}

.video-item {
    background: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.video-item:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.video-player {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    position: relative;
}

.video-player iframe {
    width: 100%;
    height: 100%;
    border: 0;
}

.video-controls {
    padding: 1rem;
    display: flex;
    justify-content: center;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
}

/* Responsive Design */
/* Tablet Landscape (1024px - 1200px) */
@media (max-width: 1200px) {
    .header-content {
        flex-wrap: wrap;
    }
    
    .title-section {
        flex: 1;
        min-width: 0;
    }
    
    .rooms-grid {
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 1.25rem;
    }
    
    .room-card {
        max-width: 100%;
    }
    
    .stats-section {
        gap: 16px;
        padding: 20px;
    }
    
    .stats-section {
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    }
    
    .stat-card {
        padding: 18px;
    }
    
    .content-area {
        padding: 0 0 2rem;
    }
    
    .video-gallery {
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    }
}

/* Tablet Portrait (768px - 1024px) */
@media (max-width: 1024px) {
    .content-area {
        padding: 0 0 1.75rem;
    }
    
    .header-section {
        padding: 16px 20px;
        margin-top: 16px;
    }
    
    .header-content {
        gap: 14px;
    }
    
    .rooms-grid {
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 1.25rem;
    }
    
    .room-card {
        max-width: 100%;
        min-width: 0;
    }
    
    .stats-section {
        padding: 18px;
        gap: 14px;
        margin-bottom: 20px;
    }
    
    .stat-card {
        min-width: calc(50% - 7px);
        padding: 16px;
    }
    
    .stat-content h3 {
        font-size: 22px;
    }
    
    .table-header {
        padding: 1.25rem 1.5rem;
    }
    
    .table-title {
        font-size: 1.125rem;
    }
    
    .modern-table {
        min-width: 700px;
    }
    
    .modern-table th,
    .modern-table td {
        padding: 0.875rem 1rem;
        font-size: 0.8125rem;
    }
    
    .video-gallery {
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 1.25rem;
    }
    
    .modal-content {
        max-width: 95vw;
    }
}

/* Screens below 1020px */
@media (max-width: 1020px) {
    .stats-section {
        padding: 16px;
        gap: 12px;
        margin-bottom: 18px;
        grid-template-columns: repeat(2, 1fr);
    }
    
    .stat-card {
        padding: 14px;
        gap: 12px;
    }
    
    .stat-icon {
        width: 42px;
        height: 42px;
    }
    
    .stat-icon svg {
        width: 21px;
        height: 21px;
    }
    
    .stat-content h3 {
        font-size: 20px;
    }
    
    .stat-content p {
        font-size: 12px;
    }
}

/* Screens below 720px - Keep key-value on same row */
@media (max-width: 720px) {
    .stats-section {
        padding: 12px;
        gap: 8px;
        margin-bottom: 16px;
        grid-template-columns: repeat(2, 1fr);
    }
    
    .stat-card {
        padding: 10px;
        gap: 8px;
    }
    
    .stat-icon {
        width: 36px;
        height: 36px;
    }
    
    .stat-icon svg {
        width: 18px;
        height: 18px;
    }
    
    .stat-content h3 {
        font-size: 16px;
    }
    
    .stat-content p {
        font-size: 12px;
    }
    
    .info-row {
        padding: 0.625rem 0;
        gap: 0.5rem;
    }
    
    .info-label {
        font-size: 0.8125rem;
        flex-shrink: 0;
        min-width: 100px;
        max-width: 45%;
    }
    
    .info-value {
        font-size: 0.8125rem;
        text-align: right;
        flex: 1;
        min-width: 0;
        max-width: 55%;
    }
}

/* Mobile Landscape / Small Tablet (600px - 768px) */
@media (max-width: 768px) {
    .content-area {
        padding: 0 0 1.5rem;
    }
    
    .header-section {
        padding: 14px 16px;
        margin-top: 12px;
    }
    
    .header-content {
        flex-direction: column;
        gap: 14px;
        align-items: stretch;
    }
    
    .title-section {
        flex: 1;
        min-width: 0;
        gap: 10px;
    }
    
    .title-icon {
        width: 36px;
        height: 36px;
        flex-shrink: 0;
    }
    
    .title-icon svg {
        width: 18px;
        height: 18px;
    }
    
    .title-text {
        min-width: 0;
        flex: 1;
    }
    
    .title-text h1 {
        font-size: 18px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    
    .title-text p {
        display: none;
    }
    
    .header-actions {
        width: 100%;
        display: flex;
        gap: 10px;
        flex-direction: row;
    }
    
    .back-btn {
        padding: 10px 16px;
        font-size: 14px;
        flex: 1;
        justify-content: center;
    }
    
    .back-btn svg {
        width: 18px;
        height: 18px;
    }
    
    .btn-primary {
        padding: 10px 16px;
        font-size: 14px;
        flex: 1;
        justify-content: center;
    }
    
    .stats-section {
        padding: 14px;
        gap: 10px;
        margin-bottom: 16px;
        grid-template-columns: repeat(2, 1fr);
    }
    
    .stat-card {
        padding: 12px;
        gap: 10px;
    }
    
    .stat-icon {
        width: 40px;
        height: 40px;
    }
    
    .stat-icon svg {
        width: 20px;
        height: 20px;
    }
    
    .stat-content h3 {
        font-size: 18px;
    }
    
    .stat-content p {
        font-size: 12px;
    }
    
    .rooms-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
        width: 100%;
    }
    
    .room-card {
        border-radius: 16px;
        max-width: 100%;
        min-width: 0;
        width: 100%;
    }
    
    .card-header {
        padding: 1.25rem 1.25rem 0.875rem;
        min-width: 0;
    }
    
    .card-body {
        padding: 0 1.25rem 1.25rem;
        min-width: 0;
    }
    
    .room-type-badge {
        max-width: calc(100% - 80px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    
    .rooms-table-container {
        border-radius: 12px;
        max-height: calc(100vh - 200px);
    }
    
    .table-header {
        padding: 1rem 1.25rem;
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }
    
    .table-title {
        font-size: 1.125rem;
    }
    
    .table-stats {
        width: 100%;
        justify-content: flex-start;
    }
    
    .table-wrapper {
        -webkit-overflow-scrolling: touch;
    }
    
    .modern-table {
        min-width: 650px;
    }
    
    .modern-table th {
        padding: 0.875rem 0.75rem;
        font-size: 0.75rem;
    }
    
    .modern-table td {
        padding: 0.875rem 0.75rem;
        font-size: 0.8125rem;
    }
    
    .room-image {
        width: 50px;
        height: 50px;
    }
    
    .video-gallery {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
    
    .modal-overlay {
        padding: 10px;
    }
    
    .modal-content {
        margin: 0;
        max-width: 100%;
        max-height: calc(100vh - 20px);
        border-radius: 12px;
    }
    
    .modal-header {
        padding: 18px;
    }
    
    .modal-title {
        font-size: 18px;
    }
    
    .modal-body {
        padding: 18px;
        max-height: calc(100vh - 200px);
    }
    
    .modal-actions {
        padding: 18px;
        flex-direction: column;
        gap: 10px;
    }
    
    .btn-primary, .btn-secondary, .btn-danger {
        width: 100%;
        justify-content: center;
        padding: 12px 20px;
    }
    
    .status-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 0.625rem;
    }
    
    .status-item {
        padding: 0.875rem;
    }
    
    .status-count {
        font-size: 1.25rem;
    }
    
    .status-label {
        font-size: 0.6875rem;
    }
}

/* Mobile Portrait (480px - 600px) */
@media (max-width: 600px) {
    .content-area {
        padding: 0 0 1.25rem;
    }
    
    .header-section {
        padding: 12px 14px;
        margin-top: 10px;
    }
    
    .header-content {
        gap: 12px;
    }
    
    .title-icon {
        width: 32px;
        height: 32px;
    }
    
    .title-icon svg {
        width: 16px;
        height: 16px;
    }
    
    .title-text h1 {
        font-size: 16px;
    }
    
    .header-actions {
        flex-direction: column;
        gap: 8px;
    }
    
    .back-btn, .btn-primary {
        width: 100%;
        padding: 10px 14px;
        font-size: 14px;
    }
    
    .stats-section {
        padding: 12px;
        gap: 8px;
        margin-bottom: 14px;
        grid-template-columns: repeat(2, 1fr);
    }
    
    .stat-card {
        padding: 10px;
        gap: 8px;
    }
    
    .stat-icon {
        width: 36px;
        height: 36px;
    }
    
    .stat-icon svg {
        width: 18px;
        height: 18px;
    }
    
    .stat-content h3 {
        font-size: 16px;
    }
    
    .stat-content p {
        font-size: 12px;
    }
    
    .rooms-grid {
        gap: 0.875rem;
        width: 100%;
    }
    
    .room-card {
        max-width: 100%;
        min-width: 0;
        width: 100%;
    }
    
    .card-header {
        padding: 1rem 1rem 0.75rem;
        min-width: 0;
    }
    
    .card-body {
        padding: 0 1rem 1rem;
        min-width: 0;
    }
    
    .info-row {
        padding: 0.625rem 0;
        gap: 0.5rem;
    }
    
    .info-label {
        font-size: 0.8125rem;
        flex-shrink: 0;
        min-width: 90px;
        max-width: 45%;
    }
    
    .info-value {
        font-size: 0.8125rem;
        text-align: right;
        flex: 1;
        min-width: 0;
        max-width: 55%;
    }
    
    .table-header {
        padding: 0.875rem 1rem;
    }
    
    .table-title {
        font-size: 1rem;
    }
    
    .modern-table {
        min-width: 600px;
    }
    
    .modern-table th,
    .modern-table td {
        padding: 0.75rem 0.625rem;
        font-size: 0.75rem;
    }
    
    .room-image {
        width: 45px;
        height: 45px;
    }
}

/* Small Mobile (below 480px) */
@media (max-width: 480px) {
    .content-area {
        padding: 0 0 1rem;
    }
    
    .header-section {
        padding: 10px 12px;
        margin-top: 8px;
    }
    
    .header-content {
        gap: 10px;
    }
    
    .title-icon {
        width: 30px;
        height: 30px;
    }
    
    .title-icon svg {
        width: 16px;
        height: 16px;
    }
    
    .title-text h1 {
        font-size: 15px;
    }
    
    .back-btn, .btn-primary {
        padding: 9px 12px;
        font-size: 12px;
    }
    
    .back-btn svg {
        width: 16px;
        height: 16px;
    }
    
    .stats-section {
        padding: 10px;
        gap: 6px;
        margin-bottom: 12px;
        grid-template-columns: repeat(2, 1fr);
    }
    
    .stat-card {
        padding: 10px;
        gap: 8px;
    }
    
    .stat-icon {
        width: 32px;
        height: 32px;
    }
    
    .stat-icon svg {
        width: 16px;
        height: 16px;
    }
    
    .stat-content h3 {
        font-size: 16px;
    }
    
    .stat-content p {
        font-size: 12px;
    }
    
    .rooms-grid {
        gap: 0.75rem;
        width: 100%;
    }
    
    .room-card {
        border-radius: 12px;
        max-width: 100%;
        min-width: 0;
        width: 100%;
    }
    
    .card-header {
        padding: 1rem 0.875rem 0.625rem;
        min-width: 0;
    }
    
    .card-body {
        padding: 0 0.875rem 0.875rem;
        min-width: 0;
    }
    
    .room-type-badge {
        max-width: calc(100% - 70px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    
    .room-type-badge {
        padding: 0.375rem 0.75rem;
        font-size: 0.8125rem;
    }
    
    .menu-btn {
        width: 32px;
        height: 32px;
        padding: 0.375rem;
    }
    
    .info-row {
        padding: 0.5rem 0;
        gap: 0.375rem;
    }
    
    .info-label {
        font-size: 0.75rem;
        flex-shrink: 0;
        min-width: 80px;
        max-width: 45%;
    }
    
    .info-value {
        font-size: 0.8125rem;
        text-align: right;
        flex: 1;
        min-width: 0;
        max-width: 55%;
    }
    
    .status-section {
        margin-top: 1rem;
        padding-top: 1rem;
    }
    
    .status-title {
        font-size: 0.8125rem;
        margin-bottom: 0.75rem;
    }
    
    .status-grid {
        grid-template-columns: 1fr;
        gap: 0.5rem;
    }
    
    .status-item {
        padding: 0.75rem;
    }
    
    .status-count {
        font-size: 1.125rem;
    }
    
    .status-label {
        font-size: 0.625rem;
    }
    
    .utensils-section {
        margin-top: 1rem;
        padding-top: 1rem;
    }
    
    .utensils-title {
        font-size: 0.8125rem;
        margin-bottom: 0.625rem;
    }
    
    .utensil-tag {
        padding: 0.25rem 0.625rem;
        font-size: 0.6875rem;
    }
    
    .table-header {
        padding: 0.75rem;
    }
    
    .table-title {
        font-size: 0.9375rem;
    }
    
    .stat-number {
        font-size: 1.25rem;
    }
    
    .stat-label {
        font-size: 0.6875rem;
    }
    
    .modern-table {
        min-width: 550px;
    }
    
    .modern-table th {
        padding: 0.625rem 0.5rem;
        font-size: 0.6875rem;
    }
    
    .modern-table td {
        padding: 0.625rem 0.5rem;
        font-size: 0.6875rem;
    }
    
    .room-image {
        width: 40px;
        height: 40px;
    }
    
    .modal-header {
        padding: 16px;
    }
    
    .modal-title {
        font-size: 16px;
    }
    
    .modal-description {
        font-size: 14px;
    }
    
    .modal-body {
        padding: 16px;
    }
    
    .modal-actions {
        padding: 16px;
    }
    
    .empty-state-content {
        padding: 2rem 1.5rem;
    }
    
    .empty-state-icon {
        width: 64px;
        height: 64px;
        margin-bottom: 1.5rem;
    }
    
    .empty-state-icon svg {
        width: 32px;
        height: 32px;
    }
    
    .empty-state-title {
        font-size: 1.25rem;
    }
    
    .empty-state-description {
        font-size: 0.875rem;
    }
    
    .empty-illustration-img {
        width: 200px;
        height: 200px;
    }
}

.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 3000;
    max-width: 380px;
}

.toast {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: #fff;
    border-left: 4px solid #ef4444;
    border-radius: 8px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
    padding: 12px 14px;
    margin-bottom: 10px;
}

.toast-error {
    border-left-color: #ef4444;
}

.toast-success {
    border-left-color: #10b981;
}

.toast-message {
    margin: 0;
    font-size: 14px;
    color: #374151;
}

.toast-close {
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.25s ease;
}

.toast-enter-from,
.toast-leave-to {
    opacity: 0;
    transform: translateX(20px);
}
</style>

